import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Quote, Star, Award } from 'lucide-react';
import { Button, CardCorner, StatBar, TranslatedText } from '../ui';
import { useLandingContent } from '../../hooks/useLandingContent';

export function SuccessStories() {
  const { t, i18n } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { data } = useLandingContent();
  const isSpanish = i18n.language === 'es';

  const successStories = useMemo(
    () => {
      const pickLocale = (es: string, en: string) => (isSpanish ? es : en) || es || en;
      if (data?.testimonials?.length) {
        return data.testimonials.map((tm) => ({
          name: tm.name,
          team: tm.team,
          country: tm.country,
          achievement: pickLocale(tm.achievement_es, tm.achievement_en),
          rating: tm.rating,
          quote: pickLocale(tm.quote_es, tm.quote_en),
          stats: tm.stats,
        }));
      }
      return [
        {
          name: 'Jose Tomas',
          team: "O'Higgins Sub 16",
          country: 'Chile',
          achievement: t('success.achievement1'),
          rating: 87,
          quote: t('success.quote1'),
          stats: { pace: 85, shooting: 78, passing: 82, dribbling: 88 },
        },
        {
          name: 'Daniela',
          team: 'Santiago Morning Sub 19',
          country: 'Chile',
          achievement: t('success.achievement2'),
          rating: 84,
          quote: t('success.quote2'),
          stats: { pace: 82, shooting: 75, passing: 86, dribbling: 84 },
        },
        {
          name: 'Franco',
          team: 'Universidad Catolica Sub 15',
          country: 'Chile',
          achievement: t('success.achievement3'),
          rating: 86,
          quote: t('success.quote3'),
          stats: { pace: 88, shooting: 80, passing: 79, dribbling: 85 },
        },
      ];
    },
    [t, data, isSpanish]
  );

  const next = () => setCurrentIndex((prev) => (prev + 1) % successStories.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + successStories.length) % successStories.length);

  const story = successStories[currentIndex];

  return (
    <section id="success-stories" className="relative py-24 md:py-32 overflow-hidden bg-transparent">
      {/* Semi-transparent overlay for readability */}
      <div className="absolute inset-0 bg-brand-darkNavy/50 backdrop-blur-[2px]" />
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #FFD700 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>
      
      {/* Glow */}
      <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-brand-yellow/5 rounded-full blur-[150px]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Award className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow uppercase tracking-wider">{t('success.badge')}</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
            <TranslatedText 
              i18nKey="success.title" 
              type="slideWords"
              duration={0.5}
              stagger={0.08}
            />
          </h2>
          <p className="text-lg md:text-xl text-dark-300">
            <TranslatedText 
              i18nKey="success.subtitle" 
              type="fadeWords"
              duration={0.4}
              stagger={0.03}
            />
          </p>
        </motion.div>

        {/* FIFA-style Player Card Carousel */}
        <div className="max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: 100, rotateY: -15 }}
              animate={{ opacity: 1, x: 0, rotateY: 0 }}
              exit={{ opacity: 0, x: -100, rotateY: 15 }}
              transition={{ duration: 0.5 }}
              className="grid lg:grid-cols-2 gap-8 items-center"
            >
              {/* Player Card */}
              <motion.div
                whileHover={{ scale: 1.02, rotateY: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="relative bg-gradient-to-br from-brand-blue via-brand-navy to-brand-darkNavy border border-brand-yellow/30 rounded-3xl p-6 overflow-hidden perspective-1000"
              >
                {/* Card decorations */}
                <CardCorner position="top-left" />
                <CardCorner position="top-right" />
                <CardCorner position="bottom-left" />
                <CardCorner position="bottom-right" />
                
                {/* Rating badge */}
                <div className="absolute top-4 right-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: 'spring' }}
                    className="w-16 h-16 rounded-xl bg-brand-gradient flex items-center justify-center shadow-glow-yellow"
                  >
                    <span className="text-2xl font-black text-brand-darkNavy">{story.rating}</span>
                  </motion.div>
                </div>
                
                {/* Player avatar placeholder */}
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full bg-brand-gradient opacity-20 blur-xl" />
                  <div className="relative w-full h-full rounded-full bg-brand-gradient flex items-center justify-center text-4xl font-black text-brand-darkNavy">
                    {story.name.charAt(0)}
                  </div>
                  {/* Stars */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${star <= 4 ? 'text-brand-yellow fill-brand-yellow' : 'text-brand-lightBlue'}`}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Player info */}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white">{story.name}</h3>
                  <p className="text-brand-yellow font-medium">{story.team}</p>
                  <p className="text-dark-500 text-sm">{story.country}</p>
                </div>
                
                {/* Achievement badge */}
                <div className="flex justify-center mb-6">
                  <div className="px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/30">
                    <span className="text-sm font-bold text-brand-yellow">{story.achievement}</span>
                  </div>
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <StatBar label={t('success.statPace')} value={story.stats.pace} />
                  <StatBar label={t('success.statShooting')} value={story.stats.shooting} />
                  <StatBar label={t('success.statPassing')} value={story.stats.passing} />
                  <StatBar label={t('success.statDribbling')} value={story.stats.dribbling} />
                </div>
                
                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 pointer-events-none"
                  animate={{ x: ['-200%', '200%'] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                />
              </motion.div>
              
              {/* Quote */}
              <div className="relative">
                <Quote className="absolute -top-4 -left-4 w-16 h-16 text-brand-yellow/10" />
                <blockquote className="text-xl md:text-2xl text-dark-200 leading-relaxed italic pl-8">
                  "{story.quote}"
                </blockquote>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-6 mt-12">
            <Button variant="ghost" onClick={prev}>
              <ChevronLeft className="w-6 h-6" />
            </Button>
            
            {/* Dots */}
            <div className="flex gap-3">
              {successStories.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-brand-yellow w-8'
                      : 'bg-brand-lightBlue hover:bg-brand-yellow/50'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>
            
            <Button variant="ghost" onClick={next}>
              <ChevronRight className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
